import { RolesEnum } from '@shared/enums/roles.enum';
import { QuantidadesMenu } from '@shared/interfaces/quantidadesMenu';

export interface MenuProntuarioBaseItem {
  label: string;
  icon: string;
  path?: string;
  role?: RolesEnum;
  personalizadoKey?: string;
  badgeKey?: keyof QuantidadesMenu;
  showBackButton?: boolean;
}

export interface MenuInicialBaseItem {
  label: string;
  icon: string;
  path?: string;
  role?: RolesEnum;
  children?: MenuInicialBaseItem[];
}

const simboloMaterialCompartilhado = {
  pacientesLeito: 'ward',
  gruposPnrf: 'groups',
  prescricao: 'prescriptions',
  listaPadrao: 'description',
  boletim: 'lab_profile',
} as const;

export const MenuIcone = {
  INICIO: 'home',
  PACIENTES: simboloMaterialCompartilhado.pacientesLeito,
  ENTRADAS_PNRF: simboloMaterialCompartilhado.gruposPnrf,
  PRESCRICOES_MEDICAS: simboloMaterialCompartilhado.prescricao,
  BOLETINS_MEDICOS: simboloMaterialCompartilhado.boletim,
  ANTIMICROBIANO: 'microbiology',
  PARECER: 'signature',
  INTERNACOES: simboloMaterialCompartilhado.pacientesLeito,
  HEMOCOMPONENTES: 'hematology',
  ACOMPANHAMENTOS: simboloMaterialCompartilhado.gruposPnrf,
  CONFIGURACOES: 'settings',
  LISTA_PADRAO: simboloMaterialCompartilhado.listaPadrao,
  INFORMACOES: 'info',
  CLASSIFICACAO_RISCO: 'medical_information',
  ATENDIMENTOS: 'local_hospital',
  ALERGIAS: 'allergies',
  EVOLUCAO: 'trending_up',
  EXAMES: 'conditions',
  NOTAS_CLINICAS: 'clinical_notes',
  REGULACAO: 'ambulance',
  RECEITUARIO: 'medication',
  ALTA: 'moving_beds',
  ANEXOS: 'attach_file',
  PAINEL: 'tv',
  FILA_ATENDIMENTO: simboloMaterialCompartilhado.gruposPnrf,
  ABRIR_PAINEL: 'dvr',
  MAPA_CIRURGICO: 'map',
  SALA_CIRURGICA: 'surgical',
  VISITAS_ACOMPANHANTES: 'hotel',
  LISTA_ACESSO: 'security',
  AGENDAMENTOS_CIRURGICOS: 'date_range',
} as const;

export type MenuIconeChave = keyof typeof MenuIcone;

export function classeIconeMaterialMenu(nomeSimbolo: string): string {
  return `material-symbols-outlined ${nomeSimbolo}`;
}

export const MENU_INICIAL_BASE: MenuInicialBaseItem[] = [
  {
    label: 'Início',
    icon: classeIconeMaterialMenu(MenuIcone.INICIO),
    path: '/',
  },
  {
    label: 'Lista de Pacientes',
    icon: classeIconeMaterialMenu(MenuIcone.PACIENTES),
    path: 'pacientes',
    role: RolesEnum.PACIENTES_INTERNADOS_VISUALIZAR,
  },
  {
    label: 'Classificações de Risco',
    icon: classeIconeMaterialMenu(MenuIcone.CLASSIFICACAO_RISCO),
    path: 'classificacoes-risco',
    role: RolesEnum.ACOMPANHAMENTO_VISUALIZAR,
  },
  {
    label: 'Entradas PNRF',
    icon: classeIconeMaterialMenu(MenuIcone.ENTRADAS_PNRF),
    path: 'entradas-pnrf',
    role: RolesEnum.PNRF,
  },
  {
    label: 'Prescrições Médicas',
    icon: classeIconeMaterialMenu(MenuIcone.PRESCRICOES_MEDICAS),
    path: 'prescricoes-medicas',
    role: RolesEnum.PRESCRICAO_VISUALIZAR,
  },
  {
    label: 'Boletins Médicos',
    icon: classeIconeMaterialMenu(MenuIcone.BOLETINS_MEDICOS),
    path: 'boletins-medicos',
    role: RolesEnum.BOLETIM_VISUALIZAR,
  },
  {
    label: 'Solicitações Antimicrobianas',
    icon: classeIconeMaterialMenu(MenuIcone.ANTIMICROBIANO),
    path: 'solicitacoes-antimicrobianos',
    role: RolesEnum.ANTIMICROBIANO_VISUALIZAR,
  },
  {
    label: 'Solicitações de Parecer',
    icon: classeIconeMaterialMenu(MenuIcone.PARECER),
    path: 'solicitacoes-parecer',
    role: RolesEnum.PARECER_VISUALIZAR,
  },
  {
    label: 'Solicitações de Internações',
    icon: classeIconeMaterialMenu(MenuIcone.INTERNACOES),
    path: 'solicitacoes-internacoes',
    role: RolesEnum.INTERNACAO_VISUALIZAR,
  },
  {
    label: 'Solicitações de Hemocomponentes',
    icon: classeIconeMaterialMenu(MenuIcone.HEMOCOMPONENTES),
    path: 'solicitacoes-hemocomponentes',
    role: RolesEnum.HEMOCOMPONENTE_VISUALIZAR,
  },
  {
    label: 'Acompanhamentos',
    icon: classeIconeMaterialMenu(MenuIcone.ACOMPANHAMENTOS),
    path: 'acompanhamentos',
    role: RolesEnum.ACOMPANHAMENTO_VISUALIZAR,
  },
  {
    label: 'Configurações Visitas',
    icon: classeIconeMaterialMenu(MenuIcone.VISITAS_ACOMPANHANTES),
    role: RolesEnum.VISITAS_ACOMPANHANTES_MANTER_REGRAS || RolesEnum.LISTA_ACESSO_MANTER,
    children: [
      {
        label: 'Regras por Entrada',
        icon: classeIconeMaterialMenu(MenuIcone.VISITAS_ACOMPANHANTES),
        path: 'regras-entrada',
        role: RolesEnum.VISITAS_ACOMPANHANTES_MANTER_REGRAS,
      },
      {
        label: 'Regras por Área',
        icon: classeIconeMaterialMenu(MenuIcone.PACIENTES),
        path: 'regras-area',
        role: RolesEnum.VISITAS_ACOMPANHANTES_MANTER_REGRAS,
      },
      {
        label: 'Listas de Acesso',
        icon: classeIconeMaterialMenu(MenuIcone.LISTA_ACESSO),
        path: 'lista-acesso',
        role: RolesEnum.LISTA_ACESSO_MANTER,
      },
    ],
  },
  {
    label: 'Agendamentos Cirúrgicos',
    icon: classeIconeMaterialMenu(MenuIcone.MAPA_CIRURGICO),
    path: 'mapa-cirurgico',
    role: RolesEnum.MAPA_CIRURGICO_VISUALIZAR,
  },
  {
    label: 'Mapa Cirúrgico',
    icon: classeIconeMaterialMenu(MenuIcone.SALA_CIRURGICA),
    path: 'salas-cirurgicas',
    role: RolesEnum.MAPA_CIRURGICO_VISUALIZAR,
  },
  {
    label: 'Configuração',
    icon: classeIconeMaterialMenu(MenuIcone.CONFIGURACOES),
    children: [
      {
        label: 'Prescrições Padrão',
        icon: classeIconeMaterialMenu(MenuIcone.PRESCRICOES_MEDICAS),
        path: 'configuracao/prescricao-padrao',
        role: RolesEnum.PRESCRICAO_VISUALIZAR,
      },
      {
        label: 'Atestados Padrão',
        icon: classeIconeMaterialMenu(MenuIcone.LISTA_PADRAO),
        path: 'configuracao/atestado-padrao',
        role: RolesEnum.ATESTADO_VISUALIZAR,
      },
      {
        label: 'Evoluções Padrão',
        icon: classeIconeMaterialMenu(MenuIcone.LISTA_PADRAO),
        path: 'configuracao/evolucao-padrao',
        role: RolesEnum.EVOLUCAO_VISUALIZAR,
      },
      {
        label: 'Receituários Padrão',
        icon: classeIconeMaterialMenu(MenuIcone.LISTA_PADRAO),
        path: 'configuracao/receituario-padrao',
        role: RolesEnum.RECEITUARIO_VISUALIZAR,
      },
      {
        label: 'Boletins Cirúrgicos Padrão',
        icon: classeIconeMaterialMenu(MenuIcone.LISTA_PADRAO),
        path: 'configuracao/boletim-cirurgico-padrao',
        role: RolesEnum.BOLETIM_CIRURGICO_VISUALIZAR,
      },
    ],
  },
];

export const MENU_PRONTUARIO_BASE: MenuProntuarioBaseItem[] = [
  { label: 'Informações Gerais', icon: classeIconeMaterialMenu(MenuIcone.INFORMACOES), path: '' },
  {
    label: 'Classificação de Risco',
    icon: classeIconeMaterialMenu(MenuIcone.CLASSIFICACAO_RISCO),
    path: 'classificacao-risco',
    badgeKey: 'qtdClassificacaoDeRisco',
  },
  {
    label: 'Atendimento Médico',
    icon: classeIconeMaterialMenu(MenuIcone.ATENDIMENTOS),
    path: 'atendimentos',
    role: RolesEnum.ATENDIMENTO_VISUALIZAR,
    badgeKey: 'qtdAtendimentoMedico',
  },
  {
    label: 'Alergias e Reações Adversas',
    icon: classeIconeMaterialMenu(MenuIcone.ALERGIAS),
    path: 'alergias',
    role: RolesEnum.ALERGIAS_VISUALIZAR,
    badgeKey: 'qtdAlergias',
    personalizadoKey: 'alergias',
  },
  {
    label: 'Prescrição Médica',
    icon: classeIconeMaterialMenu(MenuIcone.PRESCRICOES_MEDICAS),
    path: 'prescricao',
    role: RolesEnum.PRESCRICAO_VISUALIZAR,
    badgeKey: 'qtdPrescricaoMedica',
  },
  {
    label: 'Prescrições de Antibióticos',
    icon: classeIconeMaterialMenu(MenuIcone.ANTIMICROBIANO),
    path: 'prescricao-antibiotico',
    role: RolesEnum.PRESCRICAO_VISUALIZAR,
    badgeKey: 'qtdPrescricoesAntibiotico',
  },
  {
    label: 'Prescrição Quimioterapia',
    icon: classeIconeMaterialMenu(MenuIcone.PRESCRICOES_MEDICAS),
    path: 'prescricao-quimioterapia',
    role: RolesEnum.PRESCRICAO_VISUALIZAR,
    badgeKey: 'qtdPrescricaoQuimio',
  },
  {
    label: 'Evolução Profissional',
    icon: classeIconeMaterialMenu(MenuIcone.EVOLUCAO),
    path: 'evolucao',
    role: RolesEnum.EVOLUCAO_VISUALIZAR,
    badgeKey: 'qtdEvolucaoMedica',
  },
  {
    label: 'Exames Solicitados',
    icon: classeIconeMaterialMenu(MenuIcone.EXAMES),
    path: 'exames',
    role: RolesEnum.EXAME_VISUALIZAR,
    badgeKey: 'qtdSolicitacaoExame',
  },
  {
    label: 'Acompanhamento',
    icon: classeIconeMaterialMenu(MenuIcone.ENTRADAS_PNRF),
    path: 'acompanhamento',
    role: RolesEnum.PRESCRICAO_VISUALIZAR,
    badgeKey: 'qtdAcompanhamentoPrescricao',
  },
  {
    label: 'Boletim Médico',
    icon: classeIconeMaterialMenu(MenuIcone.BOLETINS_MEDICOS),
    path: 'boletim',
    role: RolesEnum.BOLETIM_VISUALIZAR,
    badgeKey: 'qtdBoletimMedico',
  },
  {
    label: 'Parecer Médico',
    icon: classeIconeMaterialMenu(MenuIcone.NOTAS_CLINICAS),
    path: 'parecer',
    role: RolesEnum.PARECER_VISUALIZAR,
    badgeKey: 'qtdParecerMedico',
  },
  {
    label: 'Solicitação de Regulação',
    icon: classeIconeMaterialMenu(MenuIcone.REGULACAO),
    path: 'regulacao',
    role: RolesEnum.REGULACAO_VISUALIZAR,
  },
  {
    label: 'Autorização de Internação',
    icon: classeIconeMaterialMenu(MenuIcone.PACIENTES),
    path: 'autorizacao-internacao',
    role: RolesEnum.INTERNACAO_VISUALIZAR,
    badgeKey: 'qtdAutorizacaoInternacao',
  },
  {
    label: 'Receituário',
    icon: classeIconeMaterialMenu(MenuIcone.RECEITUARIO),
    path: 'receituario',
    role: RolesEnum.RECEITUARIO_VISUALIZAR,
    badgeKey: 'qtdReceituario',
  },
  {
    label: 'Atestado',
    icon: classeIconeMaterialMenu(MenuIcone.LISTA_PADRAO),
    path: 'atestado',
    role: RolesEnum.ATESTADO_VISUALIZAR,
    badgeKey: 'qtdAtestado',
  },
  {
    label: 'Alta Médica',
    icon: classeIconeMaterialMenu(MenuIcone.ALTA),
    path: 'alta',
    role: RolesEnum.ALTA_VISUALIZAR,
  },
  {
    label: 'Anexo',
    icon: classeIconeMaterialMenu(MenuIcone.ANEXOS),
    path: 'anexo',
    role: RolesEnum.ANEXO_VISUALIZAR,
    badgeKey: 'qtdAnexos',
  },
  {
    label: 'Boletim Cirúrgico',
    icon: classeIconeMaterialMenu(MenuIcone.BOLETINS_MEDICOS),
    path: 'boletim-cirurgico',
    role: RolesEnum.BOLETIM_CIRURGICO_VISUALIZAR,
    badgeKey: 'qtdBoletinsCirurgicos',
  },
  {
    label: 'Agendamentos Cirúrgicos',
    icon: classeIconeMaterialMenu(MenuIcone.AGENDAMENTOS_CIRURGICOS),
    path: 'agendamentos-cirurgicos',
    role: RolesEnum.MAPA_CIRURGICO_VISUALIZAR,
    badgeKey: 'qtdMapasCirurgicos',
  },
  { label: 'Voltar', icon: 'pi pi-fw pi-arrow-left', showBackButton: true },
];
